"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { FaSearch, FaCheck } from "react-icons/fa";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-json";

const searchImplementationExample = `
// Author search with debouncing
const fetchAuthorSuggestions = useCallback(
  debounce(async (input: string) => {
    if (input.length < 3) {
      setAuthorState(prev => ({ ...prev, suggestions: [] }));
      return;
    }

    const response = await fetch.authorSuggestions(input);
    if (!response.ok) throw new Error("Failed to fetch suggestions");
    
    setAuthorState(prev => ({
      ...prev,
      suggestions: response.data.content.map((item: any) => ({
        id: item.mtid,
        name: item.label
      }))
    }));
  }, 300),
  []
);`;

export default function AuthorSearchDocs() {
  const t = useTranslations('docs.authorSearch');

  const authorSearchExample = `
// ${t('response.example.title')}
{
  "content": [
    {
      "mtid": 10012345,
      "label": "Dr. Example Name (Department)",
      "familyName": "Example",
      "givenName": "Name",
      "auxName": "Department"
    }
  ]
}`;

  const mtmtApiExample = `
// ${t('apiEndpoints.title')}

// ${t('apiEndpoints.authorSuggestions.title')}
GET https://m2.mtmt.hu/api/author
?size=200                    // ${t('apiEndpoints.authorSuggestions.params.size')}
&depth=0                     // ${t('apiEndpoints.authorSuggestions.params.depth')}
&cond=label;any;{searchTerm} // ${t('apiEndpoints.authorSuggestions.params.cond')}
&sort=familyName,asc        // ${t('apiEndpoints.authorSuggestions.params.sort')}
&sort=givenName,asc         
&labelLang=hun              // ${t('apiEndpoints.authorSuggestions.params.labelLang')}
&format=json                // ${t('apiEndpoints.authorSuggestions.params.format')}

// ${t('apiEndpoints.authorVerification.title')}
GET https://m2.mtmt.hu/api/author/{mtmtId}?format=json`;

  React.useEffect(() => {
    Prism.highlightAll();
  }, []);

  return (
    <div className="prose prose-slate max-w-none prose-sm">
        <h1 className="flex items-center gap-3 text-2xl font-bold mb-4">
          <FaSearch className="text-bme-main" />
          {t('features.title')}
        </h1>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-8">
          <p className="text-yellow-800 m-0">
            {t('overview')}
          </p>
        </div>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('features.title')}</h2>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <FaSearch className="mt-1 text-bme-main shrink-0" />
            {t('features.realtime')}
          </li>
          <li className="flex items-start gap-2">
            <FaCheck className="mt-1 text-green-500 shrink-0" />
            {t('features.validation')}
          </li>
        </ul>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('apiEndpoints.title')}</h2>
        <p>{t('apiEndpoints.authorSuggestions.description')}</p>
        <pre className="mt-4!">
          <code className="language-typescript">{mtmtApiExample}</code>
        </pre>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('implementation.search.title')}</h2>
        <p>{t('implementation.search.description')}</p>
        <pre className="mt-4!">
          <code className="language-typescript">{searchImplementationExample}</code>
        </pre>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('response.title')}</h2>
        <p>{t('response.description')}</p>
        <pre className="mt-4!">
          <code className="language-json">{authorSearchExample}</code>
        </pre>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('guidelines.title')}</h2>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-3">{t('guidelines.bestPractices.title')}</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <FaCheck className="text-green-500 mt-1 shrink-0" />
              {t('guidelines.bestPractices.minChars')}
            </li>
            <li className="flex items-start gap-2">
              <FaCheck className="text-green-500 mt-1 shrink-0" />
              {t('guidelines.bestPractices.exactId')}
            </li>
            <li className="flex items-start gap-2">
              <FaCheck className="text-green-500 mt-1 shrink-0" />
              {t('guidelines.bestPractices.checkmark')}
            </li>
          </ul>
        </div>

        <h2 className="text-xl font-bold mt-6 mb-3">{t('errors.title')}</h2>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h3 className="text-lg font-semibold mb-3 text-red-800">{t('errors.commonErrors')}</h3>
          <ul className="space-y-3">
            <li className="text-red-700">
              <strong>{t('errors.invalidId.title')}:</strong> {t('errors.invalidId.description')}
            </li>
            <li className="text-red-700">
              <strong>{t('errors.connection.title')}:</strong> {t('errors.connection.description')}
            </li>
          </ul>
        </div>
      </div>
  );
}
